import { FunctionComponent, useState } from "react";

import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";

import { Icon, MarkdownContent } from "@/components";
import { FitLevel } from "@/enum/fit-level.enum";

interface FitAssessmentBadgeProps {
	fitAssessment?: string | null;
	fitReason?: string | null;
	timestamp?: string | null;
}

const FIT_LEVEL_CONFIG: Record<
	FitLevel,
	{ label: string; badgeBg: string; badgeText: string; cardBg: string; borderClass: string }
> = {
	[FitLevel.STRONG]: {
		label: "Strong Fit",
		badgeBg: "bg-status-success-light",
		badgeText: "text-status-success",
		cardBg: "bg-status-success-light/30",
		borderClass: "border-status-success/20",
	},
	[FitLevel.MEDIUM]: {
		label: "Medium Fit",
		badgeBg: "bg-status-warning-light",
		badgeText: "text-status-warning",
		cardBg: "bg-status-warning-light/30",
		borderClass: "border-status-warning/20",
	},
	[FitLevel.WEAK]: {
		label: "Weak Fit",
		badgeBg: "bg-status-error-light",
		badgeText: "text-status-error",
		cardBg: "bg-status-error-light/30",
		borderClass: "border-status-error/20",
	},
	[FitLevel.NO_FIT]: {
		label: "No Fit",
		badgeBg: "bg-status-error-light",
		badgeText: "text-status-error",
		cardBg: "bg-status-error-light/30",
		borderClass: "border-status-error/20",
	},
};

export const FitAssessmentBadge: FunctionComponent<FitAssessmentBadgeProps> = ({
	fitAssessment,
	fitReason,
	timestamp,
}) => {
	const [isExpanded, setIsExpanded] = useState(false);

	if (!fitAssessment) {
		return null;
	}

	const config = FIT_LEVEL_CONFIG[fitAssessment as FitLevel];

	if (!config) {
		return null;
	}

	return (
		<div
			className={`flex flex-col rounded-lg border ${config.cardBg} ${config.borderClass}`}
			data-testid="fit-assessment-card"
		>
			<div className="flex items-center gap-2 px-3 py-2.5">
				<span
					className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${config.badgeBg} ${config.badgeText}`}
				>
					{config.label}
				</span>
				{fitReason && (
					<button
						className="flex items-center gap-1 text-xs text-text-meta hover:text-text cursor-pointer border-0 bg-transparent p-0"
						type="button"
						onClick={() => setIsExpanded(!isExpanded)}
					>
						<span>{isExpanded ? "Hide reason" : "Show reason"}</span>
						<Icon className="w-3 h-3" icon={isExpanded ? faChevronUp : faChevronDown} />
					</button>
				)}
				{timestamp && (
					<span className="text-xs text-text-meta ml-auto flex-shrink-0">{timestamp}</span>
				)}
			</div>
			{isExpanded && fitReason && (
				<div className="px-3 pb-2.5 border-t border-border-light">
					<MarkdownContent className="text-xs text-text-meta mt-2" content={fitReason} />
				</div>
			)}
		</div>
	);
};
